import { Sparkles, Star, Target, Users } from 'lucide-react-native';
import { useMemo } from 'react';
import { useWindowDimensions, View } from 'react-native';
import { FadeIn } from '../../../components/animations';
import type { ColoredDataPoint, DataPoint } from '../../../components/charts';
import { BarChart } from '../../../components/charts/BarChart';
import {
  ResponsiveDonutChart,
  ResponsiveLineChart,
  useMeasuredWidth,
} from '../../../components/charts/responsive';
import { Box } from '../../../components/primitives/Box';
import { Grid } from '../../../components/primitives/Grid';
import { HStack, Stack, VStack } from '../../../components/primitives/Stack';
import { Text } from '../../../components/primitives/Text';
import { useAuth } from '../../../contexts/AuthContext';
import { useTranslation } from '../../../contexts/TranslationContext';
import { useSourceLabel } from '../../../hooks/useSourceLabel';
import { useTranslatedStageName } from '../../../hooks/useTranslatedStageName';
import { colors } from '../../../theme/colors';
import { tokens } from '../../../theme/tokens';
import {
  type DashboardSummaryResponse,
  type DateRange,
  getDateRange,
  type TimeRange,
  useDashboardSummary,
  useLeadsOverTime,
} from '../hooks/useAnalyticsData';
import type { PipelineVelocityResponse } from '../hooks/usePipelineAnalytics';
import { usePipelineVelocity } from '../hooks/usePipelineAnalytics';
import { labelValue, percentValue, scoreValue, trendBadge } from '../utils/metricDisplay';
import { isOpenStage } from '../utils/stageRoles';
import { AlertBanner } from './AlertBanner';
import { TabEmptyState, TabErrorState, TabLoadingState } from './AnalyticsLoadingState';
import { AnalyticsMetricCard } from './AnalyticsMetricCard';
import { ChartCard } from './ChartCard';
import { ConversionFunnel, type FunnelStage } from './ConversionFunnel';
import { InsightCard } from './InsightCard';

interface OverviewTabProps {
  timeRange: TimeRange;
}

const SOURCE_COLORS: Record<string, string> = {
  email: colors.primary.neon,
  linkedin: colors.accent.purple.neon,
  web_form: colors.accent.blue.neon,
  scraping: colors.accent.amber.neon,
  manual: colors.status.success.main,
  api: colors.status.info.main,
};

const FALLBACK_COLORS = [
  colors.accent.amber.neon,
  colors.status.info.main,
  colors.status.success.main,
  colors.status.warning.main,
];

function getSourceColor(source: string, index: number): string {
  const normalized = source.toLowerCase().replaceAll(' ', '_');
  return SOURCE_COLORS[normalized] ?? FALLBACK_COLORS[index % FALLBACK_COLORS.length];
}

const BAR_PANEL_HEIGHT = 178;

function fadeColor(color: string, alpha: number): string {
  if (color.startsWith('hsl(')) {
    return color.replace('hsl(', 'hsla(').replace(')', `, ${alpha})`);
  }
  return color;
}

function BarChartPanel({ data, color }: Readonly<{ data: DataPoint[]; color: string }>) {
  const { width, onLayout } = useMeasuredWidth();

  return (
    <View style={{ width: '100%' }} onLayout={onLayout}>
      {width > 0 && (
        <BarChart
          data={data}
          width={width}
          height={BAR_PANEL_HEIGHT}
          color={color}
          gradientColors={[color, fadeColor(color, 0.13)]}
          showGrid={false}
          showYAxisLabels
          showValues={false}
          barRadius={6}
        />
      )}
    </View>
  );
}

export function OverviewTab({ timeRange }: Readonly<OverviewTabProps>) {
  const { currentOrganization } = useAuth();
  const orgId = currentOrganization?.id;
  const dateRange: DateRange = useMemo(() => getDateRange(timeRange), [timeRange]);

  const { data: summary, isLoading, isError, refetch } = useDashboardSummary(orgId, timeRange);
  const { data: velocity } = usePipelineVelocity(orgId, timeRange);
  const { data: leadsOverTime } = useLeadsOverTime(orgId, dateRange);

  if (isLoading) return <TabLoadingState />;
  if (isError) return <TabErrorState onRetry={refetch} />;
  if (!summary) return <TabEmptyState />;

  return (
    <OverviewContent summary={summary} velocity={velocity} leadsOverTime={leadsOverTime?.data} />
  );
}

export function OverviewContent({
  summary,
  velocity,
  leadsOverTime,
}: Readonly<{
  summary: DashboardSummaryResponse;
  velocity?: Pick<
    PipelineVelocityResponse,
    'leadVelocityRate' | 'pipelineValue' | 'leadVelocityGrowth' | 'stageValues'
  >;
  leadsOverTime?: Array<{ date: string; count: number }>;
}>) {
  const { width } = useWindowDimensions();
  const { t, getTranslation } = useTranslation();
  const translateStageName = useTranslatedStageName();
  const sourceLabel = useSourceLabel();
  const isDesktop = width >= tokens.breakpoints.desktop;
  const kpis = summary.kpis;

  const pipelineLeadCount = useMemo(() => {
    const stageValues = velocity?.stageValues;
    if (!stageValues) return null;
    return stageValues.reduce((total, stage) => total + stage.leadCount, 0);
  }, [velocity?.stageValues]);

  const metrics = useMemo(
    () => [
      {
        title: t('analytics.overview.metrics.leadVelocityRate'),
        value: percentValue(velocity ? velocity.leadVelocityRate : kpis.leadVelocityRate),
        subtitle: t('analytics.overview.metrics.leadVelocityRateSubtitle'),
        trend: trendBadge(velocity?.leadVelocityGrowth),
        sparklineData: summary.leadsTrend?.map((p) => p.count),
        metricType: 'visitors' as const,
      },
      {
        // Not money: this is the sum of leadCount x stage weight across the
        // funnel. A monetary pipeline value arrives in a later PR that adds
        // Lead.expectedValue.
        title: t('analytics.overview.metrics.pipelineScore'),
        // The velocity endpoint is authoritative when present: its null must
        // not fall through to the dashboard's copy of the same metric.
        value: scoreValue(velocity ? velocity.pipelineValue : kpis.pipelineValue),
        // The score sums a SUBSET of the org's leads — the open stages past the
        // entry one — so a four-lead org can read 0.50 off a single lead. The
        // arithmetic is right; saying nothing about the population is what made
        // the number unreadable. Falls back to the plain label when the
        // breakdown is unavailable, rather than declaring a coverage of zero.
        subtitle:
          pipelineLeadCount === null
            ? t('analytics.overview.metrics.pipelineScoreSubtitle')
            : t('analytics.overview.metrics.pipelineScoreCoverage', { count: pipelineLeadCount }),
        trend: undefined,
        metricType: 'revenue' as const,
      },
      {
        title: t('analytics.overview.metrics.winRate'),
        value: percentValue(kpis.conversionRate),
        subtitle: t('analytics.overview.metrics.winRateSubtitle'),
        trend: trendBadge(kpis.conversionGrowth),
        metricType: 'conversion' as const,
      },
    ],
    [kpis, velocity, pipelineLeadCount, summary.leadsTrend, t],
  );

  // One card, one source. The series used to come from /analytics/leads-over-time
  // and the subtitle's total from /analytics/dashboard-summary — two endpoints
  // parameterised differently, each behind its own five-minute cache, free to
  // disagree. The `??` made it worse: when the first was unavailable the chart
  // silently swapped in the other endpoint's series, so the curve and the total
  // stopped describing the same thing with nothing on screen to say so.
  const leadsChartData: DataPoint[] = useMemo(() => {
    return (leadsOverTime ?? []).map((p) => ({
      label: p.date.slice(5),
      value: p.count,
    }));
  }, [leadsOverTime]);

  // Summed from the points above rather than read from a KPI, so the subtitle
  // can only ever describe the curve it sits under.
  const leadsChartTotal = useMemo(
    () => leadsChartData.reduce((sum, point) => sum + point.value, 0),
    [leadsChartData],
  );

  const sourceChartData: ColoredDataPoint[] = useMemo(() => {
    return (summary.sourceDistribution ?? [])
      .filter((s) => s.count > 0)
      .map((s, i) => ({
        label: sourceLabel(s.source),
        value: s.count,
        color: getSourceColor(s.source, i),
      }));
  }, [summary.sourceDistribution, sourceLabel]);

  const funnelStages: FunnelStage[] = useMemo(() => {
    return (summary.stageDistribution ?? [])
      .sort((a, b) => a.position - b.position)
      .map((s) => ({
        id: s.stageId,
        name: s.stageName,
        count: s.count,
        // Conversion comes from the API, computed over the leads that were ever
        // in each stage. Occupancy cannot express it: the entry stage is empty
        // exactly when every lead has advanced out of it.
        percentage: s.conversionPct ?? null,
        color: s.color,
      }));
  }, [summary.stageDistribution]);

  const topSource = useMemo(() => {
    const sources = summary.sourceDistribution ?? [];
    if (sources.length === 0) return { source: 'N/A', count: 0 };
    return sources.reduce((max, s) => (s.count > max.count ? s : max), sources[0]);
  }, [summary.sourceDistribution]);

  // The stage holding the most OPEN leads: where work is piling up. Ranking
  // over the whole funnel made "Lost" the answer, because a funnel that loses
  // more than it qualifies has its largest population in a terminal stage.
  // Null, not "N/A": a funnel whose open stages are all empty has no busiest
  // stage to name, and an invented label is worse than an admitted blank.
  const busiestStage = useMemo(() => {
    const openStages = (summary.stageDistribution ?? []).filter(
      (s) => isOpenStage(s) && s.count > 0,
    );
    if (openStages.length === 0) return null;
    return openStages.reduce((max, s) => (s.count > max.count ? s : max), openStages[0]);
  }, [summary.stageDistribution]);

  const totalSourceLeads = sourceChartData.reduce((sum, s) => sum + s.value, 0);

  // The API counts the touches and resolves the weekday. This used to bucket
  // `leadsTrend` — leads CREATED — under a card captioned "AI + human touches",
  // so stage moves never appeared in it and a lead created on a Sunday counted
  // as Sunday activity. It also read `getDay()` in the viewer's zone off a
  // UTC-parsed date, which slid a reading onto the previous bar west of
  // Greenwich; the weekday now arrives already decided.
  //
  // Mapped over the buckets rather than over the labels, so a dictionary that
  // somehow holds fewer than seven weekday names costs a label and not a bar.
  const activityByDayData: DataPoint[] = useMemo(() => {
    const labels = getTranslation<string[]>('analytics.overview.weekdays');
    const buckets = summary.contactActivityByWeekday ?? [];
    return buckets.map((value: number, i: number) => ({ label: labels?.[i] ?? '', value }));
  }, [summary.contactActivityByWeekday, getTranslation]);

  const hasActivityByDay = activityByDayData.some((p) => p.value > 0);

  const qualification = summary.qualificationScoreDistribution;

  // Buckets arrive from the API already covering the whole 0-100 scale, zeros
  // included. A single tall bar in `0-20` is the honest answer for an org whose
  // leads all score low, so nothing here pads or reshapes it.
  const qualificationScoreData: DataPoint[] = useMemo(() => {
    return (qualification?.buckets ?? []).map((bucket) => ({
      label: bucket.label,
      value: bucket.count,
    }));
  }, [qualification]);

  const hasQualificationScores = (qualification?.scoredLeads ?? 0) > 0;
  const qualificationCoverage = t('analytics.overview.qualificationCoverage', {
    scored: qualification?.scoredLeads ?? 0,
    total: qualification?.totalLeadsInWindow ?? 0,
  });

  return (
    <VStack spacing="xl">
      <FadeIn delay={100}>
        <Grid columns={{ mobile: 1, tablet: 2, desktop: 4 }} spacing="xl">
          {metrics.map((m, i) => (
            <AnalyticsMetricCard key={m.title} {...m} index={i} />
          ))}
        </Grid>
      </FadeIn>

      {kpis.untouchedLeadsCount !== null && kpis.untouchedLeadsCount > 0 && (
        <AlertBanner
          message={t('analytics.overview.untouchedAlert', { count: kpis.untouchedLeadsCount })}
          delay={200}
        />
      )}

      <FadeIn delay={300}>
        <Stack direction={isDesktop ? 'horizontal' : 'vertical'} spacing="lg">
          {leadsChartData.length >= 2 && (
            <Box style={isDesktop ? { flex: 1, flexBasis: 0 } : undefined}>
              <ChartCard
                title={t('analytics.overview.leadsOverTime')}
                subtitle={t('analytics.overview.leadsOverTimeSubtitle', {
                  count: leadsChartTotal,
                })}
                accentColor="cyan"
                delay={350}
              >
                <ResponsiveLineChart data={leadsChartData} color={colors.primary.neon} />
              </ChartCard>
            </Box>
          )}
        </Stack>
      </FadeIn>

      <FadeIn delay={450}>
        <Stack direction={isDesktop ? 'horizontal' : 'vertical'} spacing="lg">
          {sourceChartData.length > 0 && (
            <Box style={isDesktop ? { flex: 1, flexBasis: 0 } : undefined}>
              <ChartCard
                title={t('analytics.overview.leadSources')}
                subtitle={t('analytics.overview.leadSourcesSubtitle', {
                  count: totalSourceLeads,
                })}
                accentColor="purple"
                delay={500}
              >
                <ResponsiveDonutChart data={sourceChartData} total={totalSourceLeads} />
              </ChartCard>
            </Box>
          )}
          {funnelStages.length > 0 && (
            <Box style={isDesktop ? { flex: 1, flexBasis: 0 } : undefined}>
              <ChartCard
                title={t('analytics.overview.conversionFunnel')}
                // The Funnel tab's key, not a second copy of the caption. The
                // two tabs render the SAME component off the same
                // `stageDistribution`, so one of them explaining that bars and
                // percentages measure different things while the other said
                // "Stage progression" was a drift between two sources of copy,
                // not a missing string.
                subtitle={t('analytics.funnel.conversionFunnelSubtitle')}
                accentColor="cyan"
                delay={550}
              >
                <Box style={{ minHeight: 200 }}>
                  <ConversionFunnel stages={funnelStages} />
                </Box>
              </ChartCard>
            </Box>
          )}
        </Stack>
      </FadeIn>

      {hasActivityByDay || hasQualificationScores ? (
        <FadeIn delay={570}>
          <Stack direction={isDesktop ? 'horizontal' : 'vertical'} spacing="lg">
            {hasActivityByDay && (
              <Box style={isDesktop ? { flex: 1, flexBasis: 0 } : undefined}>
                <ChartCard
                  title={t('analytics.overview.activityByDay')}
                  subtitle={t('analytics.overview.activityByDaySubtitle')}
                  delay={580}
                >
                  <BarChartPanel data={activityByDayData} color={colors.accent.blue[400]} />
                </ChartCard>
              </Box>
            )}
            {hasQualificationScores && (
              <Box style={isDesktop ? { flex: 1, flexBasis: 0 } : undefined}>
                <ChartCard
                  title={t('analytics.overview.qualificationScore')}
                  subtitle={qualificationCoverage}
                  delay={610}
                >
                  <BarChartPanel data={qualificationScoreData} color={colors.primary[400]} />
                </ChartCard>
              </Box>
            )}
          </Stack>
        </FadeIn>
      ) : (
        <TabEmptyState message={t('analytics.overview.noActivity')} />
      )}

      <FadeIn delay={660}>
        <VStack spacing="xl">
          <HStack spacing="md" align="center">
            <Box
              width={36}
              height={36}
              alignItems="center"
              justifyContent="center"
              backgroundColor={colors.primary.muted}
              overflow="hidden"
              style={{ borderRadius: 18 }}
            >
              <Sparkles color={colors.primary.neon} size={20} strokeWidth={2} />
            </Box>
            <Text variant="h6" color={colors.text.primary} style={{ fontWeight: '600' }}>
              {t('analytics.overview.insights')}
            </Text>
          </HStack>
          <Grid columns={{ mobile: 1, tablet: 2, desktop: 3 }} spacing="xl">
            <InsightCard
              icon={Target}
              title={t('analytics.overview.topSource')}
              value={sourceLabel(topSource.source)}
              description={t('analytics.overview.topSourceDescription', {
                count: topSource.count,
              })}
              delay={650}
              highlight
            />
            <InsightCard
              icon={Star}
              title={t('analytics.overview.busiestStage')}
              value={labelValue(busiestStage && translateStageName(busiestStage.stageName))}
              description={
                busiestStage
                  ? t('analytics.overview.busiestStageDescription', {
                      count: busiestStage.count,
                    })
                  : t('analytics.overview.busiestStageEmpty')
              }
              delay={700}
            />
            <InsightCard
              icon={Users}
              title={t('analytics.overview.newLeads')}
              value={`${kpis.totalLeads}`}
              description={t('analytics.overview.newLeadsDescription')}
              delay={750}
            />
          </Grid>
        </VStack>
      </FadeIn>
    </VStack>
  );
}
